import React, { useMemo } from "react";
import { Control, Controller, RegisterOptions } from "react-hook-form";
import NumberFormat from "react-number-format";

import { INumberFieldProps } from "shared/types";
import { Distance, getSeparators, Weight } from "shared/utils";
import { FieldWrapper, FieldWrapperProps } from "../FieldWrapper";
import { InputFieldProps } from "../InputField";
import { InputFieldWrapper, Suffix, StyledInputField } from "../styles";

export interface NumberFieldProps
  extends FieldWrapperProps,
    InputFieldProps,
    INumberFieldProps {
  control: Control;
  validation?: RegisterOptions;
  defaultValue?: number | Weight | Distance;
  formatValue?: (value: number) => number | Weight | Distance;
}

export const NumberField: React.FC<NumberFieldProps> = ({
  control,
  validation,
  defaultValue,
  name,
  required,
  error,
  disabled,
  readOnly,
  placeholder,
  value,
  autocomplete,
  min,
  max = Number.MAX_SAFE_INTEGER, // do not go higher then this, cannot be handled by react-number-format
  suffix,
  decimalScale = 2,
  allowNegative = min !== 0,
  onBlur,
  formatValue,
  ...rest
}) => {
  const separators = useMemo(() => getSeparators(), []);

  return (
    <Controller
      control={control}
      name={name}
      rules={validation}
      defaultValue={defaultValue}
      render={({ onChange, onBlur }) => (
        <FieldWrapper name={name} required={required} error={error} {...rest}>
          <InputFieldWrapper isDisabled={disabled || readOnly} error={!!error}>
            <StyledInputField
              as={NumberFormat}
              inputMode="numeric"
              id={name}
              value={value ?? ""}
              {...separators}
              decimalScale={decimalScale}
              allowNegative={allowNegative}
              placeholder={placeholder}
              onValueChange={({ floatValue }: { floatValue: number }) => {
                const formattedValue = formatValue
                  ? formatValue(floatValue)
                  : floatValue;
                onChange(formattedValue);
              }}
              onBlur={onBlur}
              disabled={disabled}
              readOnly={readOnly}
              autoComplete={autocomplete}
              aria-required={required}
              isAllowed={({ floatValue }: { floatValue: number }) => {
                return !(min && floatValue < min) && !(max && floatValue > max);
              }}
            />
            {suffix && <Suffix>{suffix}</Suffix>}
          </InputFieldWrapper>
        </FieldWrapper>
      )}
    />
  );
};
